import { css } from 'lit';

/**
 * Styles for the frame-detail markup emitted by `getFrameDetailsTemplate()` (option
 * group grid, nested sub-frame levels) plus the notes/validation lists that surround it,
 * when that markup is rendered inside a shadow root where the page stylesheet cannot
 * reach it.
 *
 * Shared by the row-extension components of the quote, purchase-order and fabrication-job
 * item tables (<franchisee-quote-item-expander>, <wm-purchase-order-item-expander>,
 * <wm-fabrication-job-item-expander>): they all render the output of that one helper, so
 * they have to stay in lockstep with it. Colours resolve against the app's inherited
 * custom properties declared on :root, which do cross the shadow boundary.
 */
export const frameExpanderStyles = css`
  :host {
    display: block;
    font-size: var(--wm-table-cell-font-size, 0.875rem);
    line-height: var(--wm-table-cell-line-height, 1.4);
    color: var(--wm-table-text, var(--webmodule-color-neutral-900, #1c2c3a));
  }

  .main-body {
    width: 100%;
    padding: 0.25rem 0;
  }
  .small-font {
    font-size: 0.8125rem;
  }

  /* Each expander section (the option grid, a nested-frame level, a notes list) takes the
     full width and stacks vertically. */
  .extended-options-line {
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    width: 100%;
  }
  .extended-options-line > * {
    flex: 0 0 100%;
  }

  /* Option-group grid: equal-width blocks, more columns as the viewport widens. */
  .row {
    display: flex;
    flex-wrap: wrap;
  }
  .extended-options-block {
    flex: 0 0 100%;
    margin-bottom: 0.75rem;
    padding-right: 1.5rem;
  }
  @media (min-width: 576px) {
    .extended-options-block {
      flex: 0 0 50%;
    }
  }
  @media (min-width: 768px) {
    .extended-options-block {
      flex: 0 0 33.333333%;
    }
  }
  @media (min-width: 1024px) {
    .extended-options-block {
      flex: 0 0 25%;
    }
  }
  @media (min-width: 1368px) {
    .extended-options-block {
      flex: 0 0 20%;
    }
  }
  @media (min-width: 1600px) {
    .extended-options-block {
      flex: 0 0 16.666666%;
    }
  }

  /* Group heading: a quiet uppercase caption with a hairline rule - no accent bar. */
  .extended-options-block h5 {
    margin: 0 0 0.4rem 0;
    padding-bottom: 0.25rem;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--webmodule-color-neutral-500, #64748b);
    border-bottom: 1px solid var(--wm-table-divider, var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14)));
  }
  .extended-options-block ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .extended-options-block li {
    margin: 0;
    padding: 0.1rem 0;
  }
  .attribute-label {
    font-weight: 600;
    margin-right: 0.4rem;
  }
  .attribute-value {
    color: var(--webmodule-color-neutral-600, #475569);
  }

  /* Nested sub-frame header + indented body: a neutral band and quiet rule, no blue.
     getFrameDetailsTemplate only ever emits level1 and level2. */
  h3.extended-options-face-header {
    margin: 0.5rem 0 0.25rem 0;
    font-size: 0.8125rem;
    font-weight: 700;
  }
  h4.extended-options-level-header {
    margin: 0.5rem 0;
    padding: 0.4rem 0.6rem;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--wm-table-text, var(--webmodule-color-neutral-900, #1c2c3a));
    background: var(--webmodule-color-neutral-100, #f1f5f9);
    border-radius: 0.25rem;
  }
  .extended-options-level1,
  .extended-options-level2 {
    margin-bottom: 0.5rem;
  }
  .extended-options-level1:last-child,
  .extended-options-level2:last-child {
    margin-bottom: 0;
  }
  .extended-options-level2 {
    margin-left: 0.25rem;
    padding-left: 0.6rem;
    border-left: 2px solid var(--wm-table-divider, var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14)));
  }

  /* Notes / validation messages. Severity colours are intentional (they carry meaning)
     and resolve against the app's inherited custom properties. */
  .list-group {
    margin: 0 0 0.5rem 0;
    padding: 0;
    list-style: none;
  }
  .list-group-item {
    padding: 0.4rem 0.6rem;
    margin-bottom: 2px;
    border: 1px solid var(--wm-table-divider, var(--webmodule-color-neutral-200, rgba(18, 52, 86, 0.14)));
    border-left-width: 3px;
    border-radius: 0.25rem;
  }
  .list-group-item-primary,
  .list-group-item-note {
    border-left-color: var(--webmodule-color-neutral-400, #94a3b8);
  }
  .list-group-item-info {
    border-left-color: var(--cyan, #17a2b8);
  }
  .list-group-item-warning {
    border-left-color: var(--yellow, #f0ad4e);
  }
  .list-group-item-danger {
    border-left-color: var(--red, #dc3545);
  }

  /* Bootstrap utility classes used inline by the templates (unavailable in the shadow
     root, so the few that matter are declared here). */
  .fw-bold {
    font-weight: 700;
  }
  .me-3 {
    margin-right: 0.5rem;
  }
`;
